import React, { useState, useCallback } from "react";
import {
  StyleSheet,
  Text,
  View,
  TouchableOpacity,
  FlatList,
  TextInput,
  Dimensions,
  PixelRatio,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import Colours from "../../utils/Colours";

const { width, height } = Dimensions.get("window");
const BASE_WIDTH = 375;
const BASE_HEIGHT = 812;

const dynamicFontSize = (size) => {
  const scaleFactor = Math.min(width / BASE_WIDTH, height / BASE_HEIGHT);
  const newSize = size * scaleFactor;
  return Math.round(PixelRatio.roundToNearestPixel(newSize));
};

const dynamicSpacing = (size) => (width / BASE_WIDTH) * size;
const dynamicSize = (size) => (width / BASE_WIDTH) * size;

const getInitials = (name = "") => {
  const parts = name.trim().split(" ");
  return parts.length >= 2
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
};

// const AVATAR_TINTS = [
//   ["#E6F1FB", "#185FA5"],
//   ["#E1F5EE", "#0F6E56"],
//   ["#FAEEDA", "#854F0B"],
//   ["#EEEDFE", "#3C3489"],
//   ["#FAECE7", "#993C1D"],
// ];

const AVATAR_TINTS = [
  // Tint 1: Deep brand contrast on ultra-light brand glow
  ["#ffe1d7", "#ff5630"], // [Colours.PRIMARY_LIGHTER, Colours.PRIMARY]

  // Tint 2: Mid-tier primary step-down on charcoal balance
  ["#ffe1d7", "#2e2e2e"], // [Colours.PRIMARY_LIGHTER, Colours.DARKGRAY]

  // Tint 3: Deep brand primary core on soft neutral linen
  ["#F5F5F5", "#ff5630"], // [Colours.LIGHTEST, Colours.PRIMARY]

  // Tint 4: Charcoal text backed by vibrant mid-accent peach balance
  ["#ff9e7a", "#2e2e2e"], // [Colours.PRIMARY_LIGHT, Colours.DARKGRAY]

  // Tint 5: Pure white text on high-energy primary solid container base
  ["#ff5630", "#FFFFFF"], // [Colours.PRIMARY, White]
];

const getTint = (index) => AVATAR_TINTS[index % AVATAR_TINTS.length];

const BankRow = ({ bank, index, onSelect, query }) => {
  const [bg, fg] = getTint(index);

  console.log();

  const renderName = () => {
    if (!query) return <Text style={styles.bankName}>{bank.name}</Text>;
    const lower = bank.name.toLowerCase();
    const matchIdx = lower.indexOf(query.toLowerCase());
    if (matchIdx === -1) return <Text style={styles.bankName}>{bank.name}</Text>;
    return (
      <Text style={styles.bankName}>
        {bank.name.slice(0, matchIdx)}
        <Text style={styles.highlight}>{bank.name.slice(matchIdx, matchIdx + query.length)}</Text>
        {bank.name.slice(matchIdx + query.length)}
      </Text>
    );
  };

  return (
    <TouchableOpacity
      style={styles.bankRow}
      onPress={() => onSelect(bank.name, bank.code)}
      activeOpacity={0.6}
    >
      <View style={[styles.avatar, { backgroundColor: bg }]}>
        <Text style={[styles.avatarText, { color: fg }]}>{getInitials(bank.name)}</Text>
      </View>
      <View style={styles.bankMeta}>
        {renderName()}
        <Text style={styles.bankCode}>{bank.code}</Text>
      </View>
      <Ionicons name="chevron-forward" size={16} color={Colours.DARKLIGHTGRAY} />
    </TouchableOpacity>
  );
};

const EmptyState = () => (
  <View style={styles.emptyState}>
    <Ionicons name="business-outline" size={36} color={Colours.DARKLIGHTGRAY} />
    <Text style={styles.emptyTitle}>No banks found</Text>
    <Text style={styles.emptySub}>Try a different spelling or clear the search</Text>
  </View>
);

// 🌟 FIX 1: Moved the ListHeader structural components out of the rendering loop
// We pass query, setQuery, handleClear, and filteredLength as explicit props now.
const ListHeader = ({ query, setQuery, handleClear, filteredLength }) => (
  <>
    {/* Search bar */}
    <View style={styles.searchSection}>
      <View style={styles.searchWrap}>
        <Ionicons name="search-outline" size={18} color={Colours.DARKLIGHTGRAY} />
        <TextInput
          style={styles.searchInput}
          placeholder="Search banks…"
          placeholderTextColor={Colours.DARKLIGHTGRAY}
          value={query}
          onChangeText={setQuery}
          autoCorrect={false}
          autoCapitalize="none"
          returnKeyType="search"
        />
        {query.length > 0 && (
          <TouchableOpacity
            onPress={handleClear}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Ionicons name="close-circle" size={18} color={Colours.DARKLIGHTGRAY} />
          </TouchableOpacity>
        )}
      </View>
    </View>

    {/* Section label */}
    <View style={styles.sectionRow}>
      <Text style={styles.sectionLabel}>
        {query.trim() ? `${filteredLength} result${filteredLength !== 1 ? "s" : ""}` : "All banks"}
      </Text>
    </View>
  </>
);

const BankList = ({ bankData = [], handleSelectBank }) => {
  const [query, setQuery] = useState("");

  const filtered = query.trim()
    ? bankData.filter((b) => b.name.toLowerCase().includes(query.trim().toLowerCase()))
    : bankData;

  const handleClear = useCallback(() => setQuery(""), []);

  return (
    <FlatList
      data={filtered}
      keyExtractor={(item) => item.id?.toString() ?? item.code}
      // 🌟 FIX 2: Render the isolated component cleanly with its dynamic reactive bindings
      ListHeaderComponent={
        <ListHeader
          query={query}
          setQuery={setQuery}
          handleClear={handleClear}
          filteredLength={filtered.length}
        />
      }
      ListEmptyComponent={EmptyState}
      ItemSeparatorComponent={() => <View style={styles.separator} />}
      contentContainerStyle={styles.listContent}
      keyboardShouldPersistTaps="handled"
      keyboardDismissMode="none"
      renderItem={({ item, index }) => (
        <BankRow bank={item} index={index} onSelect={handleSelectBank} query={query.trim()} />
      )}
    />
  );
};

const styles = StyleSheet.create({
  listContent: {
    flexGrow: 1,
    paddingBottom: dynamicSpacing(100),
    backgroundColor: Colours.LIGHTEST,
  },
  searchSection: {
    paddingHorizontal: dynamicSpacing(16),
    paddingVertical: dynamicSpacing(12),
    backgroundColor: "#FFFFFF",
    borderBottomWidth: 0.5,
    borderBottomColor: "#E0E0E0",
  },
  searchWrap: {
    flexDirection: "row",
    alignItems: "center",
    gap: dynamicSpacing(10),
    backgroundColor: Colours.LIGHTEST,
    borderWidth: 0.5,
    borderColor: "#E0E0E0",
    borderRadius: dynamicSize(12),
    paddingHorizontal: dynamicSpacing(12),
    height: dynamicSpacing(44),
  },
  searchInput: {
    flex: 1,
    fontSize: dynamicFontSize(15),
    fontFamily: "Sen-Regular",
    color: Colours.DARKGRAY,
    paddingVertical: 0,
  },
  sectionRow: {
    paddingHorizontal: dynamicSpacing(16),
    paddingTop: dynamicSpacing(12),
    paddingBottom: dynamicSpacing(6),
  },
  sectionLabel: {
    fontSize: dynamicFontSize(11),
    fontFamily: "Sen-Bold",
    color: Colours.DARKLIGHTGRAY,
    textTransform: "uppercase",
    letterSpacing: 0.8,
  },
  bankRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: dynamicSpacing(12),
    paddingHorizontal: dynamicSpacing(16),
    paddingVertical: dynamicSpacing(13),
    backgroundColor: "#FFFFFF",
  },
  avatar: {
    width: dynamicSpacing(36),
    height: dynamicSpacing(36),
    borderRadius: dynamicSize(8),
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  avatarText: {
    fontSize: dynamicFontSize(13),
    fontFamily: "Sen-Bold",
  },
  bankMeta: {
    flex: 1,
  },
  bankName: {
    fontSize: dynamicFontSize(15),
    fontFamily: "Sen-Regular",
    color: Colours.DARKGRAY,
  },
  bankCode: {
    fontSize: dynamicFontSize(12),
    fontFamily: "Sen-Regular",
    color: Colours.DARKLIGHTGRAY,
    marginTop: 1,
  },
  highlight: {
    backgroundColor: "#FAC775",
    color: "#412402",
    borderRadius: 2,
  },
  separator: {
    height: 0.5,
    backgroundColor: "#F0F0F0",
    marginLeft: dynamicSpacing(64),
  },
  emptyState: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: dynamicSpacing(60),
    paddingHorizontal: dynamicSpacing(32),
    gap: dynamicSpacing(8),
  },
  emptyTitle: {
    fontSize: dynamicFontSize(15),
    fontFamily: "Sen-Bold",
    color: Colours.DARKGRAY,
    marginTop: dynamicSpacing(4),
  },
  emptySub: {
    fontSize: dynamicFontSize(13),
    fontFamily: "Sen-Regular",
    color: Colours.DARKLIGHTGRAY,
    textAlign: "center",
  },
});

export default BankList;
